import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Tooltip, Typography } from 'antd';
import { AgGridReact } from 'ag-grid-react';
import { PLGrid } from '../common/PLGrid';
import { serialNumberColumn } from '../../libs/getSerialNumberColumn';
import { getActionColumn } from '../../libs/getActionColumn';
import { FormFieldConfig } from '../../types/formField.types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faTrash, faUndo } from '@fortawesome/free-solid-svg-icons';
import ExcelRowErrorModal from './ExcelRowErrorModal';
const GridPreview = ({ rowData, columnConfig, rowErrors, setRows }: any) => {

  const gridRef = useRef<AgGridReact>(null);
  const [selectedRowNum, setRowNum] = useState<number>(0);
  const [originalRowData, setOriginalRowData] = useState<any[]>([]);

  const onDelete = (rowIndex: number) => {
    setRows((prev:any) =>
      prev.map((row:any, index:number) =>
        index === rowIndex ? { ...row, excelDeleted: !row.excelDeleted } : row
      )
    );
  };

  // Set original data once (deep copy)
  useEffect(() => {
    if (originalRowData.length === 0 && rowData.length > 0) {
      const deepCopy = JSON.parse(JSON.stringify(rowData));
      setOriginalRowData(deepCopy);
    }
  }, [rowData]);

  // Important: Give AG Grid a way to uniquely identify rows
  const getRowId = useMemo(() => (params: any) => {
    return params.data._rowId ?? params.data.id ?? params.data._id ?? JSON.stringify(params.data); // fallback
  }, []);

  // Ensure each row has a stable _rowId
  const rowDataWithId = useMemo(() => {
    return rowData.map((row: any, index: number) => ({
      _rowId: index.toString(), // simple index-based ID; replace with unique key if available
      ...row,
    }));
  }, [rowData]);

  const restoreRow = (rowIndex: number) => {
    const updatedRow = { ...originalRowData[rowIndex], _rowId: rowDataWithId[rowIndex]._rowId };
    const updatedRowData = [...rowDataWithId];
    updatedRowData[rowIndex] = updatedRow;
    setRows(updatedRowData);

    gridRef.current?.api.applyTransaction({
      update: [updatedRow],
    });
  };

  const nameLabelMap = Object.fromEntries(columnConfig.map((col: FormFieldConfig) => [col.field, col.headerName]));

  const actionColumn = useMemo(() => {
    return getActionColumn<any>({
      width: 80,
      additionalActions: (_, rowIndex: any) => {
        const currentRow = { ...rowData[rowIndex] };
        const originalRow = { ...originalRowData[rowIndex] };
        const isDeleted = currentRow?.excelDeleted;

        // Remove transient props before comparison
        const { _rowId: transientId, ...currentClean } = currentRow;
        const { _rowId: originalId, ...originalClean } = originalRow;

        // Deep compare
        const isEdited = JSON.stringify(currentClean) !== JSON.stringify(originalClean);

        const showUndo = isDeleted || isEdited;
        const hasErrors = rowErrors?.[rowIndex] && Object.keys(rowErrors[rowIndex]).length > 0; 

        return (
          <>
            <span
              title={hasErrors ? "Errors" : "No Errors"}
              onClick={() => hasErrors && setRowNum(rowIndex + 1)}
              className="pl-2"
              style={{
                display: 'inline-block',
                width: '24px',
                textAlign: 'center',
                opacity: hasErrors ? 1 : 0.4,
                cursor: hasErrors ? 'pointer' : 'not-allowed',
              }}
            >
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className={hasErrors ? 'text-yellow-600 hover:text-yellow-800' : 'text-gray-300'}
              />
            </span>

            <span
              title="Delete Row"
              onClick={() => !isDeleted && onDelete(rowIndex)}
              className="pl-2"
              style={{
                display: 'inline-block',
                width: '24px',
                textAlign: 'center',
                opacity: isDeleted ? 0.4 : 1,
                cursor: isDeleted ? 'not-allowed' : 'pointer',
              }}
            >
              <FontAwesomeIcon
                icon={faTrash}
                className="text-red-600 hover:text-red-800"
              />
            </span>

            <span
              title="Restore Original Row"
              onClick={() => showUndo && restoreRow(rowIndex)}
              className="pl-2"
              style={{
                display: 'inline-block',
                width: '24px',
                textAlign: 'center',
                opacity: showUndo ? 1 : 0.4,
                cursor: showUndo ? 'pointer' : 'not-allowed',
              }}
            >
              <FontAwesomeIcon
                icon={faUndo}
                className={showUndo ? 'text-blue-600 hover:text-blue-800' : 'text-gray-400'}
              />
            </span>
          </>
        );
      },
    });
  }, [rowData, originalRowData]);


  const columnDefs = [
    serialNumberColumn,
    ...columnConfig,
    actionColumn,
  ];

  return (
    <div className="flex-1 overflow-x-auto">
      <PLGrid
        ref={gridRef}
        rowData={rowDataWithId}
        columnDefs={columnDefs}
        getRowId={getRowId}
        onCellValueChanged={(params) => {

          const updatedRow = params.data;
          const rowIndex: any = params.node.rowIndex;

          const { _rowId, __rowId, ...cleanRow } = updatedRow;
          setRows((prevRows: any) => {
            const newRows = [...prevRows];
            newRows[rowIndex] = { ...cleanRow };
            return newRows;
          });
        }}
      />
      <ExcelRowErrorModal
        rowErrors={rowErrors}
        selectedRowNum={selectedRowNum}
        setRowNum={setRowNum}
        nameLabelMap={nameLabelMap}
      />
    </div>
  );
};

export default GridPreview;
